import { useCallback, useMemo, useState } from 'react';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';

export interface SupplierResolution {
  supplierByEmail: Record<string, string>;
  supplierIdByEmail: Record<string, string>;
  /** Suppliers (by display name) for the supplied email list, de-duplicated. */
  uniqueSupplierNames: string[];
  /** Supplier ids for the supplied email list, de-duplicated. */
  uniqueSupplierIds: string[];
  lookupSupplierName: (email: string | undefined) => string | undefined;
  recordSupplier: (email: string | undefined, db: SupplierEmployeeDto) => void;
}

/**
 * Maintains the per-email mapping of supplier name / id resolved from the
 * backend. The hook deliberately takes a pre-memoized array of emails so the
 * caller controls when derived values recompute.
 */
export const useSupplierResolution = (emails: string[]): SupplierResolution => {
  const [supplierByEmail, setSupplierByEmail] = useState<Record<string, string>>({});
  const [supplierIdByEmail, setSupplierIdByEmail] = useState<Record<string, string>>({});

  const uniqueSupplierNames = useMemo(() => {
    const set = new Set<string>();
    for (const email of emails) {
      const name = supplierByEmail[email];
      if (name) set.add(name);
    }
    return [...set];
  }, [emails, supplierByEmail]);

  const uniqueSupplierIds = useMemo(() => {
    const set = new Set<string>();
    for (const email of emails) {
      const id = supplierIdByEmail[email];
      if (id) set.add(id);
    }
    return [...set];
  }, [emails, supplierIdByEmail]);

  const lookupSupplierName = useCallback(
    (email: string | undefined): string | undefined => (email ? supplierByEmail[email] : undefined),
    [supplierByEmail],
  );

  const recordSupplier = useCallback((key: string | undefined, db: SupplierEmployeeDto) => {
    if (!key) return;
    const name = db.supplier?.name;
    const id = db.supplier?.id;
    if (name) setSupplierByEmail((prev) => ({ ...prev, [key]: name }));
    if (id) setSupplierIdByEmail((prev) => ({ ...prev, [key]: id }));
  }, []);

  return {
    supplierByEmail,
    supplierIdByEmail,
    uniqueSupplierNames,
    uniqueSupplierIds,
    lookupSupplierName,
    recordSupplier,
  };
};
